import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, Fragment as _Fragment } from "vue"

const _hoisted_1 = ["data-loaded"]
const _hoisted_2 = {
  key: 0,
  class: "container align-content-center col-md-6 offset-md-3"
}
const _hoisted_3 = { key: 1 }
const _hoisted_4 = { key: 1 }
const _hoisted_5 = { key: 2 }
const _hoisted_6 = {
  key: 1,
  class: "text-center"
}

import { computed, watch, onMounted, onUnmounted, getCurrentInstance } from 'vue';
import { useStore } from 'vuex';
import { latestMessage } from '@/plugins/ws';
import { useToast } from '@/composables/useToast';
import { useLocalStorage } from '@/composables/useLocalStorage';
import { useSubscription } from '@/composables/useSubscription';
import { useConnection } from '@/composables/useConnection';
import { useRouteAccess } from '@/composables/useRouteAccess';


import Auth from './components/Auth.vue';
import TopMenu from './components/TopMenu.vue';
import DataLanguages from '@/components/data/languages.vue';
import DataCountries from '@/components/data/countries.vue';
import DataCountryLanguages from '@/components/data/countryLanguages.vue';
import DataLocales from '@/components/data/locales.vue';
import DataBackups from './components/data/backups.vue';
import DataBlockchains from './components/data/blockchains.vue';
import DataBlockchainAddresses from './components/data/blockchainAddresses.vue';
import DataCurrencies from './components/data/currencies.vue';
import DataCurrencyBlockchains from './components/data/currencyBlockchains.vue';
import DataExchangeCurrencies from './components/data/exchangeCurrencies.vue';
import DataGlobalCurrencies from './components/data/globalCurrencies.vue';
import DataGlobalPairExchanges from './components/data/globalPairExchanges.vue';
import DataGlobalPairIndicatorSets from './components/data/globalPairIndicatorSets.vue';
import DataGlobalPairs from './components/data/globalPairs.vue';
import DataIndicatorOuts from './components/data/indicatorOuts.vue';
import DataIndicatorParams from './components/data/indicatorParams.vue';
import DataIndicators from './components/data/indicators.vue';
import DataExchangePairs from './components/data/exchangePairs.vue';
import DataExchanges from './components/data/exchanges.vue';
import DataIndicatorSets from './components/data/indicatorSets.vue';
import DataPriceCurrencies from './components/data/priceCurrencies.vue';
import DataPriceExchanges from './components/data/priceExchanges.vue';
import DataSettings from './components/data/settings.vue';
import DataTradeGroups from './components/data/tradeGroups.vue';
import DataUserAccounts from './components/data/userAccounts.vue';
import DataUserWallets from './components/data/userWallets.vue';
import DataPricePairs from './components/data/pricePairs.vue';
import DataAgents from '@/components/data/agents.vue';
import DataMath from '@/components/data/math.vue';
import DataUserTelegramConnections from '@/components/data/userTelegramConnections.vue';
import DataUsers from '@/components/data/users.vue';
import DataUserAgents from '@/components/data/userAgents.vue';
import DataAdmin from '@/components/data/admin.vue';
import DataActions from '@/components/data/actions.vue';
import DataEmailTemplates from '@/components/data/emailTemplates.vue';
import DataLanguageEntities from '@/components/data/languageEntities.vue';
import DataLanguageGroups from '@/components/data/languageGroups.vue';
import DataLanguagePages from '@/components/data/languagePages.vue';
import DataActionErrors from '@/components/data/actionErrors.vue';
import DataLanguageGroupItems from '@/components/data/languageGroupItems.vue';
import DataLanguagePageItems from '@/components/data/languagePageItems.vue';
import DataTranslateActionErrors from '@/components/data/translateActionErrors.vue';
import DataTranslateEmailTemplates from '@/components/data/translateEmailTemplates.vue';
import DataTranslateLanguageEntities from '@/components/data/translateLanguageEntities.vue';
import DataTranslateLanguagePages from '@/components/data/translateLanguagePages.vue';
import DataTranslateLanguagePageItems from '@/components/data/translateLanguagePageItems.vue';
import DataTranslateLanguageGroupItems from '@/components/data/translateLanguageGroupItems.vue';
import LanguageModal from '@/components/global/LanguageModal.vue';
import {useTranslation} from "@/composables/useTranslation";
import { usePageTitle } from './composables/usePageTitle';
import {RootState} from "@/store";


export default /*@__PURE__*/_defineComponent({
  ...{
  name: 'App',
},
  __name: 'App',
  setup(__props) {



const store = useStore<RootState>();
const { showToast } = useToast();
const { setGroupTranslations, setPageTranslations } = useTranslation();
const { handleStorageUpdate, initializeFromStorage, clearBrowserData } = useLocalStorage();
const subscription = useSubscription();
const connection = useConnection();
const routeAccess = useRouteAccess();

const internalInstance = getCurrentInstance();
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const wsSend = internalInstance?.appContext.config.globalProperties.$wsSend as (signal: string, payload?: any) => void;

// --- State and Computed ---
const theme = computed(() => store.state.theme.theme);
const isLoaded = computed(() => store.state.connection.wsLoaded);
const isConnected = computed(() => store.state.connection.wsConnected);
const lastTimeBuild = computed(() => store.state.connection.lastTimeBuild);

const isSessionInit = computed(() => routeAccess.isSessionInit.value);
const isUserEmailConfirm = computed(() => routeAccess.isUserEmailConfirm.value);
const isAdminPanel = computed(() => routeAccess.isAdminPanel.value);
const isAdmin = computed(() => routeAccess.isAdmin.value);
const isGuestRoute = computed(() => routeAccess.isGuestRoute.value);
const isAdminRoute = computed(() => routeAccess.isAdminRoute.value);

usePageTitle();

// --- Methods ---
function actionLogout() {
  store.dispatch('auth/setAuthMode', 'login');
  wsSend('authLogout');
}

function actionClearBrowserDataSuccess() {
  clearBrowserData({
    $store: store,
    wsSend: wsSend,
  });

  store.dispatch('connection/setIsClearedBrowser', true);
}

function actionClearBrowserData() {
  store.dispatch('connection/setIsClearedBrowser', true);

  showToast('Данные браузера очищены', 'Данные были очищены в другой вкладке браузера', 5000);
}

function storageUpdate(event: StorageEvent) {
  handleStorageUpdate(event, {
    $store: store,
    setAuthMode: (mode: string) => store.dispatch('auth/setAuthMode', mode),
    setLastTimeBuild: (val: number | null) => store.dispatch('connection/setLastTimeBuild', val),
    selectTimeFrame: (val: string) => store.dispatch('graph/selectTimeFrame', val),
    selectCandleCount: (val: number) => store.dispatch('graph/selectCandleCount', val),
    timeframes: ["1m", "3m", "5m", "15m", "1h", "2h", "4h", "6h", "8h", "12h", "1d", "3d", "1w"],
    candleCounts: [50, 100, 200, 400],
    wsSend: wsSend,
  });
}

function resubscribePage() {
  subscription.resubscribePage();
}

// --- Watchers ---
watch(latestMessage, (data) => {
  if (!data || !data.signal) return;
  switch (data.signal) {
    case 'auth-session':
      store.dispatch('auth/wsSetUser', {
        idUser: data.auth.idUser ?? null,
        email: data.auth.email ?? null,
        name: data.auth.name ?? null,
        theme: data.auth.theme ?? theme.value,
        admin: data.auth.admin ?? null,
        confirmEmail: data.auth.confirmEmail ?? false,
        willDelete: data.auth.willDelete ?? null,
        idLanguage: data.auth.idLanguage ?? null,
        idLocale: data.auth.idLocale ?? null,
        register: data.auth.register,
        newPassword: data.auth.newPassword,
        restorePassword: data.auth.restorePassword,
        changeEmail: data.auth.changeEmail,
      });
      store.dispatch('recommendedLanguages/wsSetRecommendedLanguages', {recommendedLanguages: data['recommendedLanguages']});
      store.dispatch('i18n/setLocale', null);
      store.dispatch('i18n/setLanguage', data.i18n.idLanguage);

      if (lastTimeBuild.value === null) {
        store.dispatch('connection/setLastTimeBuild', data.lastTimeBuild);
      } else if (lastTimeBuild.value !== data.lastTimeBuild) {
        store.dispatch('connection/setLastTimeBuild', data.lastTimeBuild);
        window.location.reload();
      }
      if (subscription.validateSubscribe()) {
        // Validation passed, let the page handle its own subscription
      }
      break;
    case 'auth-update-language':
      break;
    case 'auth-login':
      store.dispatch('auth/wsSetUser', {
        idUser: data.auth.idUser,
        name: data.auth.name,
        email: data.auth.email,
        admin: data.auth.admin,
        theme: data.auth.theme,
        willDelete: data.auth.willDelete,
        confirmEmail: data.auth.confirmEmail,
        idLanguage: data.auth.idLanguage,
        idLocale: data.auth.idLocale,
        register: data.auth.register,
        newPassword: data.auth.newPassword,
        restorePassword: data.auth.restorePassword,
      });
      break;
    case 'auth-logout':
    case 'auth-delete-account':
      store.dispatch('auth/setAuthMode', 'login');
      store.dispatch('auth/wsUnsetUser');
      break;
    case 'clear-browser-data-success':
      actionClearBrowserDataSuccess();
      break;
    case 'clear-browser-data':
      actionClearBrowserData();
      break;
    case 'profile-name':
      store.dispatch('auth/profileSetName', data.newName);
      break;
    case 'profile-theme':
      store.dispatch('auth/profileSetTheme', data.newTheme);
      store.dispatch('theme/setTheme', data.newTheme);
      break;
    case 'profile-locale':
      store.dispatch('auth/profileSetLocale', data.idLocale);
      store.dispatch('auth/profileSetLanguage', data.idLanguage);
      store.dispatch('i18n/setLocale', data.idLocale);
      store.dispatch('i18n/setLanguage', data.idLanguage);
      break;
    case 'admin-user-update':
      store.dispatch('auth/adminUserUpdate', data);
      if (data.block) {
        actionLogout();
      }
      break;
    case 'profile-delete-account':
    case 'profile-delete-account-success':
      store.dispatch('auth/updateUserWillDelete', {
        willDelete: data.willDelete,
      });
      break;
    case 'profile-delete-cancel':
    case 'profile-delete-cancel-success':
      store.dispatch('auth/updateUserWillDelete', {
        willDelete: null,
      });
      break;
    case 'admin-reload':
      if (window.location.pathname !== '/admin/tasks') {
        window.location.reload();
      }
      break;
    case 'info':
      showToast('Info', data.message ?? data.reason);
      break;
  }

  if (data.i18n) {
    if (data.i18n.groups) {
      if (data.i18n.groups.session) {
        setGroupTranslations('session', data.i18n.groups.session);
      }
      if (data.i18n.groups.languageSwitcher) {
        setGroupTranslations('languageSwitcher', data.i18n.groups.languageSwitcher);
      }
    }
    if (data.i18n.page) {
      setPageTranslations(data.i18n.page);
    }
  }
});

// --- Lifecycle ---
onMounted(() => {
  // Initialize state from localStorage
  initializeFromStorage({
    $store: store,
    setAuthMode: (mode: string) => store.dispatch('auth/setAuthMode', mode),
    setLastTimeBuild: (val: number | null) => store.dispatch('connection/setLastTimeBuild', val),
    selectTimeFrame: (val: string) => store.dispatch('graph/selectTimeFrame', val),
    selectCandleCount: (val: number) => store.dispatch('graph/selectCandleCount', val),
    timeframes: ["1m", "3m", "5m", "15m", "1h", "2h", "4h", "6h", "8h", "12h", "1d", "3d", "1w"],
    candleCounts: [50, 100, 200, 400],
    wsSend: wsSend,
  });
  // Setup connection and subscription
  connection.setupConnection();
  subscription.onMounted();
  window.addEventListener('storage', storageUpdate);
});

onUnmounted(() => {
  window.removeEventListener('storage', storageUpdate);
  connection.cleanup();
  subscription.onUnmounted();
});

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!
  const _component_router_view = _resolveComponent("router-view")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(DataAdmin),
    _createVNode(DataActions),
    _createVNode(DataEmailTemplates),
    _createVNode(DataLanguageEntities),
    _createVNode(DataLanguageGroups),
    _createVNode(DataLanguagePages),
    _createVNode(DataActionErrors),
    _createVNode(DataLanguageGroupItems),
    _createVNode(DataLanguagePageItems),
    _createVNode(DataTranslateActionErrors),
    _createVNode(DataTranslateEmailTemplates),
    _createVNode(DataTranslateLanguageEntities),
    _createVNode(DataTranslateLanguagePages),
    _createVNode(DataTranslateLanguagePageItems),
    _createVNode(DataTranslateLanguageGroupItems),
    _createVNode(DataBackups),
    _createVNode(DataAgents),
    _createVNode(DataBlockchains),
    _createVNode(DataBlockchainAddresses),
    _createVNode(DataCountries),
    _createVNode(DataCountryLanguages),
    _createVNode(DataCurrencies),
    _createVNode(DataCurrencyBlockchains),
    _createVNode(DataExchangeCurrencies),
    _createVNode(DataExchangePairs),
    _createVNode(DataExchanges),
    _createVNode(DataGlobalCurrencies),
    _createVNode(DataGlobalPairExchanges),
    _createVNode(DataGlobalPairIndicatorSets),
    _createVNode(DataGlobalPairs),
    _createVNode(DataIndicatorOuts),
    _createVNode(DataIndicatorParams),
    _createVNode(DataIndicators),
    _createVNode(DataIndicatorSets),
    _createVNode(DataLanguages),
    _createVNode(DataLocales),
    _createVNode(DataPriceCurrencies),
    _createVNode(DataPriceExchanges),
    _createVNode(DataPricePairs),
    _createVNode(DataSettings),
    _createVNode(DataTradeGroups),
    _createVNode(DataUsers),
    _createVNode(DataUserAccounts),
    _createVNode(DataUserWallets),
    _createVNode(DataUserTelegramConnections),
    _createVNode(DataUserAgents),
    _createVNode(DataMath),
    _createVNode(LanguageModal),
    _cache[6] || (_cache[6] = _createElementVNode("div", {
      class: "toast-container position-fixed bottom-0 end-0 p-3",
      style: {"z-index":"1055"},
      id: "toastContainer"
    }, null, -1)),
    _createElementVNode("div", {
      class: "container",
      id: "mainContainer",
      "data-loaded": isLoaded.value
    }, [
      _createVNode(TopMenu, {
        isAdminPanel: isAdminPanel.value,
        onActionLogout: actionLogout
      }, null, 8, ["isAdminPanel"]),
      (!isConnected.value || !isSessionInit.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_2, _cache[0] || (_cache[0] = [
            _createElementVNode("div", {
              class: "spinner-border",
              role: "status"
            }, [
              _createElementVNode("span", { class: "visually-hidden" }, "Loading...")
            ], -1)
          ])))
        : (_openBlock(), _createElementBlock("div", _hoisted_3, [
            ((!isGuestRoute.value && (isUserEmailConfirm.value === false)))
              ? (_openBlock(), _createBlock(Auth, {
                  key: 0,
                  onResubscribePage: resubscribePage
                }))
              : ((isAdminRoute.value && (isAdminPanel.value === true) && (!isAdmin.value)))
                ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                    _cache[2] || (_cache[2] = _createElementVNode("h2", null, "Админка", -1)),
                    _cache[3] || (_cache[3] = _createElementVNode("p", null, "У вас нет доступа сюда", -1)),
                    _createVNode(_component_router_link, { to: { name: 'Main'} }, {
                      default: _withCtx(() => _cache[1] || (_cache[1] = [
                        _createTextVNode("На главную")
                      ])),
                      _: 1
                    })
                  ]))
                : (_openBlock(), _createElementBlock("div", _hoisted_5, [
                    (isConnected.value && isSessionInit.value)
                      ? (_openBlock(), _createBlock(_component_router_view, { key: 0 }))
                      : _createCommentVNode("", true),
                    (!isLoaded.value)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_6, _cache[4] || (_cache[4] = [
                          _createElementVNode("div", {
                            class: "spinner-border",
                            role: "status"
                          }, [
                            _createElementVNode("span", { class: "visually-hidden" }, "Loading...")
                          ], -1)
                        ])))
                      : _createCommentVNode("", true)
                  ]))
          ])),
      _cache[5] || (_cache[5] = _createElementVNode("footer", { class: "mt-auto" }, [
        _createElementVNode("hr")
      ], -1))
    ], 8, _hoisted_1)
  ], 64))
}
}

})